import { useCallback, useEffect, useState } from "react";
import { InlineSpinner } from "../components/InlineSpinner";
import { TeacherBackLink, TeacherMessagePage } from "../components/TeacherChrome";
import {
  fetchArFrames,
  fetchArWorks,
  revokeArKeyring,
  stageArKeyring,
  uploadArMarker,
  type ArFramesResponse,
  type ArStagedKeyring,
  type ArWorkRow,
} from "../lib/arApi";
import { compileMarker } from "../lib/arMarker";
import {
  TeacherApiError,
  TeacherLoginRequiredError,
  TeacherRetryableError,
  teacherRequest,
} from "../lib/teacherApi";

interface Printables {
  short_id: string;
  version: number;
  published: boolean;
  scan_url: string;
  thumbnail_url: string;
  qr_url: string;
}

type Stage = "idle" | "frames" | "staging" | "compiling" | "uploading";

const stageLabels: Record<Exclude<Stage, "idle">, string> = {
  frames: "장면 고르는 중",
  staging: "그림과 QR 만드는 중",
  compiling: "마커 만드는 중",
  uploading: "키링 발행하는 중",
};

/**
 * 교사 «AR 키링 만들기» 화면.
 *
 * 흐름은 넷이다: 후보 보기 → 장면 고르기 → (브라우저가) 마커 컴파일 → 발행.
 * 🔑 마커를 **교사 브라우저가** 만든다. 서버에 브라우저를 상주시키지 않기로 했기 때문이다
 *    (배치 결정 A+C). 백필처럼 여러 편을 한꺼번에 만들 때는 로컬 CLI 를 쓴다.
 */
export function TeacherArPage({ sessionId }: { sessionId: string }) {
  const [works, setWorks] = useState<ArWorkRow[] | null>(null);
  const [fatal, setFatal] = useState<"login" | "forbidden" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openWorkId, setOpenWorkId] = useState<string | null>(null);
  const [frames, setFrames] = useState<ArFramesResponse | null>(null);
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState(0);
  const [printables, setPrintables] = useState<Record<string, Printables>>({});
  const [bulk, setBulk] = useState<{ done: number; total: number; name: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await fetchArWorks(sessionId);
      setWorks(response.works);
      setError(null);
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else if (caught instanceof TeacherApiError && caught.status === 403) setFatal("forbidden");
      else setError(caught instanceof TeacherRetryableError
        ? "연결이 불안정해요. 다시 불러와 주세요."
        : "작품 목록을 불러오지 못했습니다.");
    }
  }, [sessionId]);

  useEffect(() => { void load(); }, [load]);

  if (fatal) return <TeacherMessagePage kind={fatal} />;

  const eligible = (works ?? []).filter((work) => work.has_final_video);

  const openFrames = async (work: ArWorkRow): Promise<void> => {
    setOpenWorkId(work.work_id);
    setFrames(null);
    setError(null);
    setStage("frames");
    try {
      setFrames(await fetchArFrames(work.work_id));
    } catch (caught) {
      setError(caught instanceof TeacherApiError ? caught.message : "장면을 불러오지 못했어요.");
      setOpenWorkId(null);
    } finally {
      setStage("idle");
    }
  };

  /**
   * 고른 장면으로 키링을 만든다.
   *
   * 🚨 세 걸음(확정 → 컴파일 → 발행)이 **한 번의 누름** 안에서 일어난다. 중간에서 멈추면
   *    «장면은 골랐는데 마커가 없는» 상태로 남고, 그때 스캔하면 카메라만 켜지고 아무 일도
   *    안 일어난다. 그래서 실패하면 어느 걸음에서 멈췄는지를 화면이 말한다.
   */
  const makeKeyring = async (
    workId: string,
    frameSec: number,
    markerScore?: number | null,
  ): Promise<void> => {
    setError(null);
    setProgress(0);
    let staged: ArStagedKeyring;
    try {
      setStage("staging");
      staged = await stageArKeyring(workId, frameSec, markerScore);
    } catch (caught) {
      setStage("idle");
      setError(caught instanceof TeacherApiError ? caught.message : "그림을 만들지 못했어요.");
      throw caught;
    }
    try {
      setStage("compiling");
      // 🚨 같은 출처 경로로 받는다. 서명 URL 을 fetch 하면 R2 에 CORS 가 없어 통째로 막힌다.
      const response = await fetch(`/api/ar/works/${encodeURIComponent(workId)}/thumbnail`, {
        headers: await authHeader(),
      });
      if (!response.ok) throw new Error("THUMBNAIL_FETCH_FAILED");
      const marker = await compileMarker(await response.blob(), {
        onProgress: (percent) => setProgress(Math.round(percent)),
      });
      setStage("uploading");
      await uploadArMarker(workId, marker);
    } catch (caught) {
      setStage("idle");
      setError(caught instanceof Error
        ? `마커를 만들지 못했어요. ${caught.message}`
        : "마커를 만들지 못했어요.");
      throw caught;
    }
    setStage("idle");
    setOpenWorkId(null);
    setFrames(null);
    setPrintables((current) => ({
      ...current,
      [workId]: {
        short_id: staged.short_id,
        version: staged.version,
        published: true,
        scan_url: staged.scan_url,
        thumbnail_url: staged.thumbnail_url,
        qr_url: staged.qr_url,
      },
    }));
    await load();
  };

  /**
   * 반 전체를 추천 장면으로 한 번에 만든다.
   *
   * 🚨 **한 명씩 차례로** 한다. 동시에 돌리면 브라우저에서 마커 컴파일(편당 수 초, WebGL)이
   *    겹쳐 탭이 멈춘다. 그리고 한 명이 실패해도 **거기서 멈춘다** — 조용히 건너뛰면
   *    「반 전체 만들었다」고 생각한 채 몇 명의 키링이 없는 상태로 인쇄에 들어간다.
   * 🔑 «추천» 은 규칙 점수(무늬가 많은 장면)일 뿐 사람의 선택이 아니다. 그래서 이 버튼은
   *    기본 동선이 아니라 «급할 때» 다 — 화면이 그렇게 말한다.
   */
  const makeAllRecommended = async (): Promise<void> => {
    setError(null);
    setBulk({ done: 0, total: 0, name: "" });
    const pending = eligible.filter((work) => !work.published && work.remaining_versions > 0);
    setBulk({ done: 0, total: pending.length, name: "" });
    for (const [index, work] of pending.entries()) {
      setBulk({ done: index, total: pending.length, name: work.student_name });
      try {
        const candidates = await fetchArFrames(work.work_id);
        const pick = candidates.candidates.find((candidate) => candidate.recommended);
        if (!pick) {
          setError(`${work.student_name} 은(는) 추천할 장면을 찾지 못했어요. 직접 골라 주세요.`);
          break;
        }
        await makeKeyring(work.work_id, pick.frame_sec, pick.score);
      } catch {
        setError((current) => current
          ?? `${work.student_name} 에서 멈췄어요. 여기까지는 만들어졌어요.`);
        break;
      }
    }
    setBulk(null);
    await load();
  };

  const revoke = async (workId: string, name: string): Promise<void> => {
    if (!window.confirm(
      `${name} 의 키링을 회수할까요?\n`
      + "이미 나눠 준 종이를 비춰도 아무것도 안 나오게 돼요. 종이 자체는 되돌릴 수 없어요.",
    )) return;
    try {
      await revokeArKeyring(workId);
      await load();
    } catch (caught) {
      setError(caught instanceof TeacherApiError ? caught.message : "회수하지 못했어요.");
    }
  };

  const showPrintables = async (workId: string): Promise<void> => {
    try {
      const data = await teacherRequest<Printables>(
        `/api/ar/works/${encodeURIComponent(workId)}/printables`,
      );
      setPrintables((current) => ({ ...current, [workId]: data }));
    } catch (caught) {
      setError(caught instanceof TeacherApiError ? caught.message : "인쇄물을 불러오지 못했어요.");
    }
  };

  return (
    <main className="teacher-page">
      <div className="teacher-page-inner">
      <header className="teacher-page-heading">
        <div>
        <TeacherBackLink />
        <span className="teacher-eyebrow">AR 키링</span>
        <h1 tabIndex={-1}>AR 키링 만들기</h1>
        <p>
          완성한 영상에서 <b>인쇄할 그림</b>을 하나 고르면, 그 그림에 폰 카메라를 비출 때
          영상이 재생되는 키링이 만들어져요.
        </p>
        {/*
          🚨 이 경고를 빼면 안 된다. 수업 «종료» 는 키링을 죽이지 않지만 «삭제» 는 죽인다
             (mvs_delete_session 은 종류를 안 가리고 전부 지운다). 이미 나눠 준 종이가
             가리키는 곳이 사라지는 건 되돌릴 수 없다.
        */}
        <p className="teacher-warning">
          키링을 나눠 준 뒤에 <b>이 수업을 삭제하면 키링이 모두 멈춰요.</b>
          수업이 «종료» 되는 것은 괜찮아요 — 키링은 수업보다 오래 살아요.
        </p>
        </div>
      </header>

      {eligible.some((work) => !work.published) ? (
        <div className="ar-bulk">
          <button
            type="button"
            className="button-secondary"
            disabled={stage !== "idle" || bulk !== null}
            onClick={() => void makeAllRecommended()}
          >
            추천 장면으로 아직 없는 것 다 만들기
          </button>
          <span className="teacher-hint">
            추천은 «무늬가 많은 장면» 을 규칙으로 고른 것이에요. 급할 때만 쓰고, 되도록 직접 골라 주세요.
          </span>
          {bulk ? (
            <p className="teacher-hint" aria-live="polite">
              {bulk.total}명 중 {bulk.done}명 완료{bulk.name ? ` — ${bulk.name} 만드는 중` : ""}
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="teacher-error-banner">{error}</p> : null}
      {works === null ? <p className="teacher-empty">작품을 불러오는 중...</p> : null}
      {works !== null && eligible.length === 0 ? (
        <p className="teacher-empty">완성한 영상이 아직 없어요. 영상이 완성되면 키링을 만들 수 있어요.</p>
      ) : null}

      <ul className="ar-work-list">
        {eligible.map((work) => {
          const printable = printables[work.work_id];
          return (
            <li key={work.work_id} className="ar-work-card">
              <div className="ar-work-head">
                <strong>{work.student_name}</strong>
                {work.is_teacher_demo ? <span className="teacher-demo-badge">시연작</span> : null}
                {work.published
                  ? <span className="ar-work-state published">키링 있음 v{work.version}</span>
                  : work.revoked
                    ? <span className="ar-work-state">회수됨</span>
                    : <span className="ar-work-state">아직 없음</span>}
                <span className="ar-work-state">앞으로 {work.remaining_versions}번 더</span>
              </div>
              <div className="ar-work-actions">
                <button
                  type="button"
                  disabled={stage !== "idle" || bulk !== null || work.remaining_versions === 0}
                  onClick={() => void openFrames(work)}
                >
                  {work.remaining_versions === 0
                    ? "더 만들 수 없어요"
                    : work.published ? "다시 만들기" : "장면 고르기"}
                </button>
                {work.published ? (
                  <button type="button" onClick={() => void revoke(work.work_id, work.student_name)}>
                    키링 회수
                  </button>
                ) : null}
                {work.published && !printable ? (
                  <button type="button" onClick={() => void showPrintables(work.work_id)}>
                    인쇄물 받기
                  </button>
                ) : null}
              </div>

              {printable ? (
                <div className="ar-printables">
                  <img src={printable.thumbnail_url} alt={`${work.student_name} 키링 그림`} />
                  <img src={printable.qr_url} alt={`${work.student_name} 키링 QR`} />
                  <div>
                    <p className="ar-scan-url">{printable.scan_url}</p>
                    {/*
                      🚨 내려받기는 **앵커**로 한다. 서명 URL 을 `fetch` 하면 R2 에 CORS 가 없어
                         전면 차단된다(2026-08-18 «영상 저장» 사고 PR #132). 최상위 이동은 CORS 밖이다.
                    */}
                    <a href={printable.thumbnail_url}>그림 내려받기</a>
                    <a href={printable.qr_url}>QR 내려받기</a>
                  </div>
                </div>
              ) : null}

              {openWorkId === work.work_id ? (
                <div className="ar-frame-picker">
                  {stage === "frames" ? <InlineSpinner label="장면을 뽑는 중..." /> : null}
                  {frames && frames.failed_seconds.length > 0 ? (
                    // 실패를 숨기지 않는다 — «6장 중 4장» 을 교사가 알아야 한다.
                    <p className="teacher-hint">
                      {frames.candidates.length}장만 뽑았어요(못 뽑은 지점 {frames.failed_seconds.length}곳).
                    </p>
                  ) : null}
                  <div className="ar-frame-grid">
                    {(frames?.candidates ?? []).map((candidate) => (
                      <button
                        key={candidate.frame_sec}
                        type="button"
                        className="ar-frame-option"
                        disabled={stage !== "idle"}
                        onClick={() => void makeKeyring(work.work_id, candidate.frame_sec, candidate.score)}
                      >
                        <img src={candidate.preview} alt={`${candidate.frame_sec}초 장면`} />
                        <span>
                          {candidate.frame_sec}초
                          {candidate.recommended ? <b className="ar-recommended"> 추천</b> : null}
                        </span>
                      </button>
                    ))}
                  </div>
                  {stage !== "idle" && stage !== "frames" ? (
                    <p className="teacher-hint" aria-live="polite">
                      {stageLabels[stage]}{stage === "compiling" && progress > 0 ? ` ${progress}%` : ""}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      </div>
    </main>
  );
}

/**
 * 썸네일을 받을 때만 쓰는 인증 헤더.
 * 🔑 `teacherRequest` 는 JSON 을 돌려주는 통로라 바이너리에는 못 쓴다 — 토큰만 빌려 온다.
 */
async function authHeader(): Promise<Record<string, string>> {
  const { readTeacherTokensFromCookie } = await import("../lib/teacherApi");
  const tokens = readTeacherTokensFromCookie();
  return tokens ? { authorization: `Bearer ${tokens.access_token}` } : {};
}
